/**
 * ContractsSidebar — slide-in list of all family contracts (#73).
 *
 * Anchored to the left edge of the viewport. Trigger is a vertically-centred
 * "ousko" tab handle glued to the right edge of the panel; clicking it toggles
 * the panel open/closed. The handle stays attached to the panel itself, so it
 * slides out together with the panel — the same affordance closes it.
 *
 * Data:
 *   - Reads `useContracts()` (all family contracts, no per-member filter).
 *   - Member names resolved via `useFamily().data.members`.
 *
 * Sort order (per the issue): non-expired contracts first, then expired,
 * each group ordered by `valid_to` desc.
 *   - "Expired" is derived from `valid_to < today` — the BE's
 *     `ContractStatus` enum only tracks the confirm flow
 *     (`pending_confirm | confirmed`) and doesn't encode expiry.
 *   - `valid_to` is `'YYYY-MM-DD'`, which lexically compares as a calendar
 *     date, so string compare is sufficient.
 *
 * Z-index: panel z-[80] sits above the scene (max z-[60]) and above the
 * user chip (z-[31]), but below upload/login modals (z-[100]+). The scrim
 * sits at z-[78].
 */
import { useEffect, useState, type ReactNode } from 'react'
import { AnimatePresence, motion } from 'framer-motion'
import { Link } from 'react-router-dom'
import { FileText, Upload } from 'lucide-react'
import type { Contract } from '@shared/schemas'
import { useContracts, useFamily } from '@/hooks/queries'
import { useSession } from '@/state/session'
import { AREA_ICON } from '@/components/pictograms'
import { czechAreaLabel } from '@/components/OcrPreview'

export default function ContractsSidebar() {
  const [open, setOpen] = useState(false)
  const contractsQuery = useContracts()
  const familyQuery = useFamily()
  const openModal = useSession((s) => s.openModal)

  const contracts = contractsQuery.data ?? []
  const sorted = sortContracts(contracts)

  useEffect(() => {
    if (!open) return
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') setOpen(false)
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [open])

  function memberName(id: string): string {
    return familyQuery.data?.members.find((m) => m.id === id)?.name ?? '—'
  }

  return (
    <>
      <AnimatePresence>
        {open && (
          <motion.div
            key="contracts-sidebar-scrim"
            data-testid="contracts-sidebar-scrim"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.18, ease: 'linear' }}
            onClick={() => setOpen(false)}
            className="fixed inset-0 z-[78] bg-pop-night/30 backdrop-blur-[1px]"
            aria-hidden
          />
        )}
      </AnimatePresence>

      <motion.aside
        data-testid="contracts-sidebar"
        initial={false}
        animate={{ x: open ? '0%' : '-100%' }}
        transition={{ duration: 0.24, ease: [0, 0, 0, 1] }}
        className="fixed inset-y-0 left-0 z-[80] flex w-[88vw] max-w-[360px] md:w-[360px]"
        aria-label="Seznam smluv"
      >
        <div className="relative flex h-full w-full flex-col border-r-[3px] border-pop-silhouette bg-pop-cream shadow-pixel-lg">
          <div className="flex items-center justify-between border-b-[3px] border-pop-silhouette bg-pop-silhouette px-4 py-3 font-mono text-xs uppercase tracking-wider text-pop-cream">
            <span>Moje smlouvy</span>
            <span className="opacity-70">{contracts.length}</span>
          </div>

          <div className="flex-1 overflow-y-auto">
            {contractsQuery.isLoading ? (
              <SkeletonRows />
            ) : contractsQuery.isError ? (
              <ErrorState onRetry={() => contractsQuery.refetch()} />
            ) : contracts.length === 0 ? (
              <EmptyState
                onUpload={() => {
                  setOpen(false)
                  openModal('house')
                }}
              />
            ) : (
              <ul className="flex flex-col">
                {sorted.map((c) => (
                  <li key={c.id}>
                    <ContractRow
                      contract={c}
                      holderName={memberName(c.holder_member_id)}
                      onNavigate={() => setOpen(false)}
                    />
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>

        <button
          type="button"
          data-testid="contracts-sidebar-handle"
          aria-label={open ? 'Zavřít seznam smluv' : 'Otevřít seznam smluv'}
          aria-expanded={open}
          onClick={() => setOpen((v) => !v)}
          className="absolute right-0 top-1/2 flex -translate-y-1/2 translate-x-full items-center gap-1.5 border-[3px] border-l-0 border-pop-silhouette bg-pop-torch px-2 py-4 font-mono text-[10px] uppercase tracking-wider text-pop-silhouette shadow-pixel-sm transition-colors hover:bg-pop-dawn"
          style={{ writingMode: 'vertical-rl' }}
        >
          <FileText className="h-3.5 w-3.5" strokeWidth={3} />
          <span>Moje smlouvy</span>
          {contracts.length > 0 && (
            <span className="bg-pop-silhouette px-1 py-0.5 text-[10px] leading-none text-pop-cream">
              {contracts.length}
            </span>
          )}
        </button>
      </motion.aside>
    </>
  )
}

function ContractRow({
  contract,
  holderName,
  onNavigate,
}: {
  contract: Contract
  holderName: string
  onNavigate: () => void
}) {
  const Icon = AREA_ICON[contract.type] ?? FileText
  const expired = isExpired(contract)
  return (
    <Link
      to={`/contract/${contract.id}`}
      onClick={onNavigate}
      data-testid="contracts-sidebar-row"
      className={`block border-b-[2px] border-pop-sand-dark px-4 py-3 transition-colors hover:bg-pop-sand-light ${
        expired ? 'opacity-60' : ''
      }`}
    >
      <div className="flex items-start gap-3">
        <div className="shrink-0 border-[3px] border-pop-silhouette bg-pop-torch p-1.5">
          <Icon className="h-5 w-5 text-pop-silhouette" strokeWidth={2.5} />
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <span className="truncate text-sm font-semibold uppercase tracking-wide text-pop-silhouette">
              {contract.insurer}
            </span>
            <StatusBadge contract={contract} />
          </div>
          <div className="truncate text-[11px] text-pop-shadow">
            {czechAreaLabel(contract.type)} · {holderName}
          </div>
          <div className="mt-1 flex items-center justify-between gap-2 font-mono text-[10px] uppercase tracking-wider text-pop-sand-dark">
            <span>
              {contract.valid_from} – {contract.valid_to}
            </span>
            <span className="text-pop-silhouette">
              {contract.premium_czk.toLocaleString('cs-CZ')} Kč
            </span>
          </div>
        </div>
      </div>
    </Link>
  )
}

function StatusBadge({ contract }: { contract: Contract }) {
  if (isExpired(contract)) return <Badge tone="muted">Propadlo</Badge>
  if (contract.status === 'pending_confirm') return <Badge tone="warn">Nepotvrzeno</Badge>
  return <Badge tone="ok">Aktivní</Badge>
}

function Badge({
  children,
  tone,
}: {
  children: ReactNode
  tone: 'ok' | 'warn' | 'muted'
}) {
  const tones = {
    ok: 'bg-pop-covered text-pop-cream',
    warn: 'bg-pop-caution text-pop-silhouette',
    muted: 'bg-pop-sand-dark text-pop-cream',
  } as const
  return (
    <span
      className={`shrink-0 px-1.5 py-0.5 font-mono text-[9px] uppercase tracking-wider ${tones[tone]}`}
    >
      {children}
    </span>
  )
}

function SkeletonRows() {
  return (
    <ul className="flex flex-col">
      {Array.from({ length: 5 }).map((_, i) => (
        <li key={i} className="border-b-[2px] border-pop-sand-dark px-4 py-3">
          <div className="flex animate-pulse items-start gap-3">
            <div className="h-9 w-9 shrink-0 bg-pop-sand-dark" />
            <div className="flex flex-1 flex-col gap-2">
              <div className="h-3 w-2/3 bg-pop-sand-dark" />
              <div className="h-2 w-1/2 bg-pop-sand-dark/70" />
              <div className="h-2 w-3/4 bg-pop-sand-dark/50" />
            </div>
          </div>
        </li>
      ))}
    </ul>
  )
}

function EmptyState({ onUpload }: { onUpload: () => void }) {
  return (
    <div className="flex flex-col items-center justify-center gap-4 px-6 py-12 text-center">
      <div className="border-[3px] border-pop-silhouette bg-pop-sand-light p-3">
        <FileText className="h-7 w-7 text-pop-silhouette" strokeWidth={2.5} />
      </div>
      <p className="text-sm text-pop-shadow">Zatím tu žádné smlouvy nejsou.</p>
      <button onClick={onUpload} className="pixel-btn text-xs">
        <Upload className="h-3.5 w-3.5" strokeWidth={3} />
        Nahrát smlouvu
      </button>
    </div>
  )
}

function ErrorState({ onRetry }: { onRetry: () => void }) {
  return (
    <div className="flex flex-col items-center justify-center gap-3 px-6 py-12 text-center">
      <p className="text-sm text-pop-shadow">Smlouvy se nepodařilo načíst.</p>
      <button onClick={onRetry} className="pixel-btn-secondary text-xs">
        Zkusit znovu
      </button>
    </div>
  )
}

function isExpired(c: Contract): boolean {
  const today = new Date().toISOString().slice(0, 10)
  return c.valid_to < today
}

function sortContracts(contracts: Contract[]): Contract[] {
  const today = new Date().toISOString().slice(0, 10)
  return [...contracts].sort((a, b) => {
    const aExpired = a.valid_to < today
    const bExpired = b.valid_to < today
    if (aExpired !== bExpired) return aExpired ? 1 : -1
    return b.valid_to.localeCompare(a.valid_to)
  })
}
